import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useUpdateUserProfileMutation } from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { inter } from "@/lib/fonts";
import {
  IUser,
  updateProfileSchema,
  IUpdateProfileInput,
  OtpReason,
} from "muminsmart-utils";
import PhoneInputField from "@/components/common/PhoneInputField";
import ContactOtpVerification, {
  OtpVerificationState,
  normalizePhoneNumber,
} from "@/app/components/auth/signup/ContactOtpVerification";

interface ProfileEditFormProps {
  user: IUser;
}

export default function ProfileEditForm({ user }: ProfileEditFormProps) {
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const form = useForm<IUpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      fullName: user.fullName,
      phoneNumber: user.phoneNumber,
      its: "",
    },
  });

  const [phoneVerification, setPhoneVerification] =
    useState<OtpVerificationState>({
      isVerified: false,
      verifiedOtp: null,
      value: "",
    });

  const isSubmitting = form.formState.isSubmitting;
  const watchedPhoneNumber = form.watch("phoneNumber");
  const normalizedExistingPhoneNumber = normalizePhoneNumber(
    user.phoneNumber || "",
  );
  const normalizedPhoneNumber = normalizePhoneNumber(watchedPhoneNumber || "");
  const isPhoneNumberChanged =
    normalizedPhoneNumber !== normalizedExistingPhoneNumber;
  const isPhoneNumberVerified =
    !isPhoneNumberChanged ||
    (phoneVerification.isVerified &&
      phoneVerification.value === normalizedPhoneNumber);

  const onSubmit = async (values: IUpdateProfileInput) => {
    if (isPhoneNumberChanged && !isPhoneNumberVerified) {
      toast.error("Please verify your new phone number before continuing.");
      return;
    }

    try {
      const data = await updateUserProfile({
        fullName: values.fullName.trim(),
        phoneNumber: values.phoneNumber.trim(),
        its: values.its.trim(),
      }).unwrap();

      toast.success("Profile updated successfully");
      form.reset({
        fullName: data.fullName || values.fullName,
        phoneNumber: data.phoneNumber || values.phoneNumber,
        its: data.its || values.its,
      });
    } catch (error: unknown) {
      toast.error(getRtkQueryErrorMessage(error, "Failed to update profile"));
    }
  };

  const formClassName = useMemo(() => `space-y-5 ${inter.className}`, []);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={formClassName}>
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">Full Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Enter your full name"
                  className="rounded-full p-5 border-muted bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormItem>
          <FormLabel className="font-medium">Email</FormLabel>
          <FormControl>
            <Input
              value={user.email}
              readOnly
              disabled
              className="rounded-full p-5 border-muted bg-white"
            />
          </FormControl>
        </FormItem>

        <FormField
          control={form.control}
          name="phoneNumber"
          render={() => (
            <FormItem>
              <ContactOtpVerification
                label="Phone Number"
                value={normalizedPhoneNumber}
                channel="mobile"
                reason={OtpReason.MOBILE_VERIFICATION}
                disabled={isSubmitting}
                verifiedText="Phone number verified"
                defaultVerifiedValue={normalizedExistingPhoneNumber}
                onValidateField={() => form.trigger("phoneNumber")}
                onVerifiedStateChange={setPhoneVerification}
              >
                <FormControl>
                  <Controller
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <PhoneInputField
                        country={"in"}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        inputClass={`!w-full !rounded-full !h-12 !pl-14 !pr-4 !text-base ${
                          form.formState.errors.phoneNumber
                            ? "!border-destructive"
                            : ""
                        }`}
                        buttonClass="!border-none !bg-transparent [&_.selected-flag]:!bg-transparent !pl-3"
                        containerClass="!w-full"
                        dropdownClass="!rounded-md"
                      />
                    )}
                  />
                </FormControl>
                <FormMessage />
              </ContactOtpVerification>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="its"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium">ITS Number</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="12345678"
                  className="rounded-full p-5 border-muted bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-full bg-[#C18A2A] text-white"
        >
          {isSubmitting ? "Updating..." : "Update Profile"}
        </Button>
      </form>
    </Form>
  );
}
